import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { getActiveEntitySlug } from '../utils/activeEntity';
import { slugFromDisplayName } from '../utils/vectorScenarioRegistry';
import {
  buildSubmissionPayload,
  buildNt8AlertDataSourceConfig,
  withStrategyInstrument,
  dataNameFromDisplayName,
} from '../utils/strategySubmissionForm';
import {
  buildClientMessageBody,
  buildClientSetupPacket,
  parseScenarioWebhookUrl,
} from '../utils/webhookClientPacket';
import StrategyNameEditor from '../components/admin/StrategyNameEditor';
import MetricInfo from '../components/MetricInfo';
import {
  PREVIEW_METRICS, PREVIEW_WINDOWS, formatPreviewMetric, previewMetrics,
} from '../utils/strategyPreviewMetrics';
import { BRAND_STRATEGIES_PANE_KEY as PANE_KEY, OPEN_ADD_STRATEGY_KEY } from '../utils/strategiesPageLinks';
import './BrandStrategies.css';

// What's arriving on each webhook, shown as this page's "Webhook activity" tab.
const WebhookActivity = lazy(() => import('./StrategyWebhookActivity'));

/**
 * Strategies (Brand Admins): one place to add a strategy, connect its live data
 * and choose what members see. Replaces the old Create strategy, New live
 * strategy, Equity Strategies and Webhooks pages; Strategy Data opens from here.
 *
 * Server: /api/admin/strategies* (routes/admin.js), every route limited to the
 * caller's own brand.
 */

const KINDS = [
  {
    id: 'futures',
    title: 'Futures',
    subtitle: 'NinjaTrader',
    blurb: 'Trades from NinjaTrader. Upload past trades (TradesViz or NinjaTrader export) and/or send live trade alerts.',
    symbolLabel: 'Futures symbol',
    symbolPlaceholder: 'ES, NQ, MNQ',
    csvHelp: 'TradesViz “Trades” export or a NinjaTrader executions export.',
    feedType: 'webhook_nt8_alert',
    category: 'futures',
  },
  {
    id: 'stocks',
    title: 'Stock / ETF',
    subtitle: 'TradingView',
    blurb: 'A stock or ETF strategy with TradingView order alerts. Upload its “List of trades” for history.',
    symbolLabel: 'Ticker',
    symbolPlaceholder: 'TSLA, QQQ',
    csvHelp: 'TradingView “List of trades” export.',
    feedType: 'webhook_tradingview',
    category: 'stocks',
  },
  {
    id: 'stock_trades',
    title: 'Stock trades',
    subtitle: 'Each buy & sell',
    blurb: 'Your system sends every buy and sell with its real symbol. RiskLo pairs them into closed trades with P&L and tracks open positions.',
    symbolLabel: 'Main ticker or label (optional)',
    symbolPlaceholder: 'NVDA, Tech book',
    csvHelp: '',
    feedType: 'webhook_trade_fills',
    category: 'stocks',
    liveOnly: true,
  },
  {
    id: 'equity_curve',
    title: 'Daily balance',
    subtitle: 'Equity curve',
    blurb: 'One account balance per day. Upload the history, then send each day’s balance.',
    symbolLabel: 'Ticker or label',
    symbolPlaceholder: 'QLD, MyBook',
    csvHelp: 'Two columns: date and balance (equity).',
    feedType: 'webhook_kairos',
    category: 'stocks',
  },
];

const KIND_LABEL = { futures: 'Futures', stock: 'Stock / ETF', trades: 'Stock trades', equity: 'Daily balance' };

function authJson() {
  return { 'Content-Type': 'application/json', ...getBearerAuthHeaders() };
}

function ago(iso) {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

/** Where live data comes from when it's a mapping on Admin → Webhooks, not this strategy's own URL. */
function mappedSource(row) {
  const parts = [];
  if (row.nt8Names?.length) parts.push(`NinjaTrader: ${row.nt8Names.join(' + ')}`);
  if (row.tvMapped) parts.push('TradingView');
  return parts.join(' · ');
}

function LiveStatus({ row }) {
  const via = mappedSource(row);
  const viaLabel = via ? <span className="bs-muted bs-small"> · via {via}</span> : null;
  if (row.liveStatus === 'live') {
    return (
      <span className="bs-status is-live" title={`${row.dataCount} updates received`}>
        ● Live{row.lastDataAt ? ` · ${ago(row.lastDataAt)}` : ''}{viaLabel}
      </span>
    );
  }
  if (row.liveStatus === 'waiting') {
    return (
      <span className="bs-status is-waiting">
        Waiting for first data{row.lastTestAt ? ` · test OK ${ago(row.lastTestAt)}` : ''}{viaLabel}
      </span>
    );
  }
  if (row.source === 'data') {
    return (
      <span className="bs-status">
        {row.fromSheet ? 'From the Results Spreadsheet' : 'Uploaded results only'}
        {row.lastDataAt ? <span className="bs-muted bs-small"> · updated {ago(row.lastDataAt)}</span> : null}
      </span>
    );
  }
  return <span className="bs-status">Uploaded results only</span>;
}

/** Feed type for the copy-paste message, from the strategy's kind. */
function feedTypeForKind(kind) {
  if (kind === 'futures') return 'webhook_nt8_alert';
  if (kind === 'stock') return 'webhook_tradingview';
  if (kind === 'trades') return 'webhook_trade_fills';
  return 'webhook_kairos';
}

/** URL + exact message to paste + Send test alert. */
function LiveSetup({ url, kind, name, symbol, slug, onTested }) {
  const [copied, setCopied] = useState('');
  const [test, setTest] = useState(null);
  const { token } = parseScenarioWebhookUrl(url);
  const message = buildClientMessageBody(feedTypeForKind(kind), {
    displayName: name,
    webhookStrategyId: slug,
    kairosSymbol: symbol,
  }, { secret: token });
  const packet = buildClientSetupPacket({ messageBody: message, webhookUrl: url });

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 2000);
    } catch (_e) {
      setCopied('Couldn’t copy; select the text and copy it by hand.');
    }
  };

  const sendTest = async () => {
    setTest({ busy: true });
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `The URL answered ${res.status}`);
      setTest({ ok: true, text: 'Test alert received. Your URL works (nothing was stored).' });
      if (onTested) onTested();
    } catch (err) {
      setTest({ ok: false, text: `Test failed: ${err.message}` });
    }
  };

  const where = kind === 'futures'
    ? 'In NinjaTrader, set your strategy’s trade alert (webhook) to this URL.'
    : kind === 'stock'
      ? 'In TradingView, open the strategy’s alert → Notifications → Webhook URL, and paste the message below into the alert message.'
      : kind === 'trades'
        ? 'Have your system POST every buy and sell to this URL, one message per fill, in the format below. "action" is "buy" or "sell"; sells with no open position open a short. Several fills can go in one message as { "trades": [ … ] }. Add "id" (your order or fill id) to make retries safe.'
        : 'Have your system POST each day’s balance to this URL, in the format below.';

  return (
    <div className="bs-live">
      <p className="bs-muted">{where}</p>
      <label className="bs-field">
        <span>Webhook URL</span>
        <div className="bs-copy-row">
          <input readOnly value={url} onFocus={(e) => e.target.select()} />
          <button type="button" className="bs-btn" onClick={() => copy(url, 'URL copied')}>Copy</button>
        </div>
      </label>
      {message ? (
        <label className="bs-field">
          <span>Message to send{kind === 'futures' ? ' (example of what NinjaTrader sends)' : ''}</span>
          <pre className="bs-code">{message}</pre>
        </label>
      ) : null}
      <div className="bs-actions">
        <button type="button" className="bs-btn" onClick={() => copy(packet, 'Setup copied (URL + message)')}>Copy setup for my team</button>
        <button type="button" className="bs-btn bs-btn-primary" onClick={sendTest} disabled={test?.busy}>
          {test?.busy ? 'Sending…' : 'Send test alert'}
        </button>
      </div>
      {copied ? <p className="bs-note" role="status">{copied}</p> : null}
      {test && !test.busy ? <p className={`bs-note ${test.ok ? 'is-ok' : 'is-bad'}`} role="status">{test.text}</p> : null}
      <p className="bs-muted bs-small">Treat this URL like a password: anyone with it can send data to this strategy.</p>
    </div>
  );
}

function Modal({ title, onClose, children, wide = false }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="bs-modal-root" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" className="bs-modal-backdrop" aria-label="Close" onClick={onClose} />
      <div className={`bs-modal${wide ? ' is-wide' : ''}`}>
        <div className="bs-modal-head">
          <h3>{title}</h3>
          <button type="button" className="bs-icon-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** "Connect live data" for an existing strategy. */
const fmtMoney = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** When members see each trade's symbol (protects the strategy from being reverse engineered). */
function TradeDisplay({ slug, initial }) {
  const [delayDays, setDelayDays] = useState(String(initial?.delayDays ?? 0));
  const [windowMonths, setWindowMonths] = useState(String(initial?.windowMonths ?? 0));
  const [note, setNote] = useState('');
  const save = async () => {
    setNote('');
    try {
      const res = await fetch(API_ENDPOINTS.adminBrandStrategyTradeDisplay(slug), {
        method: 'PUT', headers: authJson(), body: JSON.stringify({ delayDays: Number(delayDays), windowMonths: Number(windowMonths) }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t save');
      setNote('Saved.');
    } catch (err) {
      setNote(err.message);
    }
  };
  return (
    <div className="bs-tradedisplay">
      <h4>Trade symbols members see</h4>
      <div className="bs-tradedisplay-row">
        <label>Hide for the first
          <input type="number" min="0" max="365" value={delayDays} onChange={(e) => setDelayDays(e.target.value)} /> days
        </label>
        <label>Then show for
          <input type="number" min="0" max="120" value={windowMonths} onChange={(e) => setWindowMonths(e.target.value)} /> months (0 = always)
        </label>
        <button type="button" className="bs-btn" onClick={save}>Save</button>
        {note ? <span className="bs-muted bs-small">{note}</span> : null}
      </div>
      <p className="bs-muted bs-small">Outside that window a trade shows as “Hidden”; its P&amp;L, date and the curve stay complete. E.g. 0 days and 3 months = a rolling 3 months of symbols.</p>
    </div>
  );
}

/**
 * Turning on a strategy's backtested / live split (the switch on its row).
 *
 * When the data says where live trading started (a live / backtest column in
 * the CSV, or the webhook marking each day), that date is offered and, when
 * chosen, followed if the data changes; otherwise a date is picked by hand.
 * Closing without saving leaves the split off.
 */
function LiveSplitDialog({ row, onClose, onSaved }) {
  const detected = row.detectedLiveSince || null;
  const [useData, setUseData] = useState(Boolean(detected) && (row.liveSinceFromData || !row.liveSince));
  const [date, setDate] = useState(row.liveSince || detected || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setError('');
    setBusy(true);
    try {
      const body = detected && useData ? { fromData: true } : { liveSince: date };
      const res = await fetch(API_ENDPOINTS.adminBrandStrategyLiveSince(row.slug), {
        method: 'PUT', headers: authJson(), body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t save');
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Backtested and live · ${row.name}`} onClose={onClose}>
      <p className="bs-muted">
        Members see this strategy’s results split into Live, Backtested and Full Journey on Results and the Overview,
        and the numbers follow what they pick. Everything before the live date counts as backtested.
      </p>
      <div className="bs-livesince-choice">
        {detected ? (
          <label className="bs-livesince-toggle">
            <input type="radio" name="livesplit" checked={useData} disabled={busy} onChange={() => setUseData(true)} />
            <span>
              Use the date from the data: <strong>{detected}</strong>
              <span className="bs-muted bs-small"> (marked live / backtest in the CSV or webhook; followed if it changes)</span>
            </span>
          </label>
        ) : (
          <p className="bs-muted bs-small">The data doesn’t mark where live trading started, so pick the date.</p>
        )}
        {detected ? (
          <label className="bs-livesince-toggle">
            <input type="radio" name="livesplit" checked={!useData} disabled={busy} onChange={() => setUseData(false)} />
            <span>Pick a date</span>
          </label>
        ) : null}
        {!useData || !detected ? (
          <div className="bs-tradedisplay-row">
            <label>Live trading started on
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} autoFocus />
            </label>
          </div>
        ) : null}
      </div>
      {error ? <p className="bs-note is-bad" role="alert">{error}</p> : null}
      <div className="bs-actions">
        <button type="button" className="bs-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="bs-btn bs-btn-primary" onClick={save} disabled={busy || (!(detected && useData) && !date)}>
          {busy ? 'Saving…' : 'Turn on'}
        </button>
      </div>
    </Modal>
  );
}

/** A trades-fed strategy: open positions, recent fills, closed trades so far. */
function FillsSummary({ slug }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    fetch(API_ENDPOINTS.adminBrandStrategyFills(slug), { headers: getBearerAuthHeaders() })
      .then((r) => r.json())
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setData({ success: false }); });
    return () => { live = false; };
  }, [slug]);
  if (!data) return <p className="bs-muted">Loading trades…</p>;
  if (!data.success || !data.applies) return null;
  return (
    <div className="bs-fills">
      <TradeDisplay slug={slug} initial={data.tradeDisplay} />
      <p className="bs-muted">
        {data.fills.toLocaleString()} fills received · {data.closedTrades.toLocaleString()} closed trades · realized P&amp;L {fmtMoney(data.realizedPnl)}
      </p>
      <h4>Open positions</h4>
      {data.openPositions.length ? (
        <table className="bs-table">
          <thead><tr><th>Symbol</th><th>Side</th><th>Quantity</th><th>Avg price</th><th>Opened</th></tr></thead>
          <tbody>
            {data.openPositions.map((p) => (
              <tr key={p.symbol}><td>{p.symbol}</td><td>{p.side}</td><td>{p.quantity.toLocaleString()}</td><td>{fmtMoney(p.averagePrice)}</td><td>{new Date(p.openedAt).toLocaleString()}</td></tr>
            ))}
          </tbody>
        </table>
      ) : <p className="bs-muted bs-small">None. Every position is closed.</p>}
      {data.recentFills.length ? (
        <>
          <h4>Latest fills</h4>
          <table className="bs-table">
            <thead><tr><th>Time</th><th>Symbol</th><th>Action</th><th>Quantity</th><th>Price</th></tr></thead>
            <tbody>
              {data.recentFills.map((f, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={i}><td>{new Date(f.time).toLocaleString()}</td><td>{f.symbol}</td><td>{f.side}</td><td>{f.quantity.toLocaleString()}</td><td>{fmtMoney(f.price)}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </div>
  );
}

function ConnectLive({ row, onClose, onChanged, onViewFeed }) {
  const [state, setState] = useState({ loading: true });

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.adminBrandStrategyWebhook(row.slug), { headers: getBearerAuthHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t load the webhook');
      setState({ ...data, loading: false });
    } catch (err) {
      setState({ loading: false, error: err.message });
    }
  }, [row.slug]);

  useEffect(() => { load(); }, [load]);

  const connect = async (replacing) => {
    if (replacing && !window.confirm('Get a new URL? The old URL stops working, so update NinjaTrader / TradingView with the new one.')) return;
    setState((s) => ({ ...s, busy: true, error: '' }));
    try {
      const res = await fetch(API_ENDPOINTS.adminBrandStrategyConnectLive(row.slug), { method: 'POST', headers: authJson() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t connect live data');
      setState((s) => ({ ...s, busy: false, hasWebhook: true, url: data.url }));
      onChanged();
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: err.message }));
    }
  };

  return (
    <Modal title={`Live data · ${row.name}`} onClose={onClose} wide>
      {state.loading ? <p className="bs-muted">Loading…</p> : null}
      {state.error ? <p className="bs-note is-bad" role="alert">{state.error}</p> : null}
      {!state.loading && state.url ? (
        <>
          <LiveSetup url={state.url} kind={row.kind} name={row.name} symbol={row.symbol} slug={row.slug} onTested={onChanged} />
          <button type="button" className="bs-link" onClick={() => connect(true)} disabled={state.busy}>Get a new URL instead</button>
        </>
      ) : null}
      {!state.loading && row.kind === 'trades' ? <FillsSummary slug={row.slug} /> : null}
      {!state.loading && (state.hasWebhook || mappedSource(row)) ? (
        <button type="button" className="bs-link" onClick={() => onViewFeed({ slug: row.slug, name: row.name })}>
          View incoming data on Webhook activity →
        </button>
      ) : null}
      {!state.loading && !state.url && state.hasWebhook ? (
        <div>
          <p className="bs-muted">This strategy’s URL was created before URLs could be shown again. Get a new one to see it here.</p>
          <button type="button" className="bs-btn bs-btn-primary" onClick={() => connect(true)} disabled={state.busy}>
            {state.busy ? 'Working…' : 'Get a new URL'}
          </button>
        </div>
      ) : null}
      {!state.loading && mappedSource(row) ? (
        <p className="bs-note">
          Already mapped on Admin → Webhooks ({mappedSource(row)}
          {row.mappedFrom ? `, counted from ${row.mappedFrom}` : ''}). Those trades add on top of your uploaded results
          {state.hasWebhook ? ' together with this URL' : ', so you don’t need a URL here'}.
          {' '}Send each NinjaTrader strategy to one place only. Sending the same trades to the shared feed and to this URL counts them twice.
        </p>
      ) : null}
      {!state.loading && !state.hasWebhook && !state.error ? (
        <div>
          <p className="bs-muted">
            Give this strategy its own webhook URL. Live trades then add on top of the results you uploaded.
          </p>
          <button type="button" className="bs-btn bs-btn-primary" onClick={() => connect(false)} disabled={state.busy}>
            {state.busy ? 'Connecting…' : 'Connect live data'}
          </button>
        </div>
      ) : null}
    </Modal>
  );
}

/** The 3-step "Add strategy" dialog. */
const fmtDay = (iso) => (iso ? new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—');

/**
 * The uploaded history's metrics before the strategy is created: the
 * Overview comparison table's metrics for All time, the last year and the
 * year to date (counted back from the file's last day).
 */
function CsvPreview({ preview }) {
  if (!preview || preview.status === 'idle') return null;
  if (preview.status === 'loading') return <p className="bs-muted bs-small bs-preview-note">Reading the file…</p>;
  if (preview.status === 'error') return <p className="bs-note is-bad bs-preview-note">{preview.error}</p>;
  const { metrics, model, liveStart, tradeDays } = preview;
  const negative = (m, v) => v != null && (m.kind === 'pct' ? v < 0 : false);
  return (
    <div className="bs-preview">
      <div className="bs-preview-head">
        <strong>Preview</strong>
        <span className="bs-muted bs-small">
          {fmtDay(metrics.firstDate)} → {fmtDay(metrics.lastDate)}
          {tradeDays ? ` · ${tradeDays} trading days` : ''}
          {' · '}{model === 'fixed' ? 'on $100,000 fixed (futures P&L)' : 'compounded equity curve'}
          {liveStart ? ` · live from ${fmtDay(liveStart)}` : ''}
        </span>
      </div>
      <div className="bs-preview-wrap">
        <table className="bs-preview-table">
          <thead>
            <tr>
              <th scope="col">Metric</th>
              {PREVIEW_WINDOWS.map((w) => (
                <th scope="col" key={w.id}>
                  {w.label}
                  {metrics.windows[w.id] ? <small>{fmtDay(metrics.windows[w.id].from)}</small> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_METRICS.map((m) => (
              <tr key={m.key}>
                <th scope="row"><span className="bs-preview-metric">{m.label}<MetricInfo label={m.label} text={m.info} /></span></th>
                {PREVIEW_WINDOWS.map((w) => {
                  const v = metrics.windows[w.id]?.[m.key];
                  return (
                    <td key={w.id} className={negative(m, v) || (m.kind === 'dd' && v) ? 'is-neg' : ''}>
                      {metrics.windows[w.id] ? formatPreviewMetric(v, m.kind) : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="bs-muted bs-small">
        The same metrics as the Overview’s comparison table; hover ⓘ to see how each is calculated.
        {model === 'fixed' ? ' Futures P&L is added in dollars on a fixed $100,000 (not compounded).' : ' The balance compounds day to day.'}
        {' '}1 Year and YTD count back from the file’s last day. Nothing is saved until you create the strategy.
      </p>
    </div>
  );
}

function AddStrategy({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [kindId, setKindId] = useState('futures');
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [csv, setCsv] = useState({ text: '', name: '', rows: 0 });
  const [wantLive, setWantLive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [preview, setPreview] = useState({ status: 'idle' });

  const kind = KINDS.find((k) => k.id === kindId);
  const hasCsv = Boolean(csv.text.trim());

  // Preview the file's metrics as soon as it's picked (parsed on the server
  // exactly as creating the strategy would; nothing is saved).
  useEffect(() => {
    if (!hasCsv || kind.liveOnly) { setPreview({ status: 'idle' }); return undefined; }
    let cancelled = false;
    setPreview({ status: 'loading' });
    fetch(API_ENDPOINTS.adminBrandStrategyPreviewCsv, {
      method: 'POST', headers: authJson(), body: JSON.stringify({ kind: kindId, csv: csv.text }),
    })
      .then((res) => res.json().then((d) => ({ ok: res.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled) return;
        if (!ok || !d.success) throw new Error(d.error || 'Couldn’t read that file.');
        setPreview({ status: 'ready', model: d.model, liveStart: d.liveStart, tradeDays: d.tradeDays, metrics: previewMetrics(d.points, d.model) });
      })
      .catch((err) => { if (!cancelled) setPreview({ status: 'error', error: err.message }); });
    return () => { cancelled = true; };
  }, [hasCsv, csv.text, kindId, kind.liveOnly]);
  // CSV-based stock strategies are daily-balance curves; live TradingView orders
  // need a webhook-only stock strategy for now.
  const liveAllowedWithCsv = kindId !== 'stocks';

  const pickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const rows = Math.max(0, text.split(/\r?\n/).filter((l) => l.trim()).length - 1);
      setCsv({ text, name: file.name, rows });
    };
    reader.onerror = () => setError('Couldn’t read that file.');
    reader.readAsText(file);
    e.target.value = '';
  };

  const next = () => {
    if (step === 0) {
      if (!name.trim()) { setError('Give the strategy a name.'); return; }
      if (!symbol.trim() && !kind.liveOnly) { setError(`Enter the ${kind.symbolLabel.toLowerCase()}.`); return; }
    }
    setError('');
    setStep((s) => s + 1);
  };

  const create = async () => {
    if (!hasCsv && !wantLive) { setError('Upload past results or connect live data (or both).'); return; }
    setBusy(true);
    setError('');
    try {
      const entitySlug = getActiveEntitySlug() || 'vector';
      const base = slugFromDisplayName(name);
      const slug = entitySlug === 'vector' ? base : `${entitySlug}_${base}`.slice(0, 64);
      const dataName = dataNameFromDisplayName(name);
      const buildDataSourceConfig = () => {
        let config;
        if (kind.feedType === 'webhook_nt8_alert') config = buildNt8AlertDataSourceConfig(name, name);
        else if (kind.feedType === 'webhook_tradingview') config = { strategyName: dataName, strategyId: slug };
        // Trades keep their real symbols: no display symbol, which would replace every trade's.
        else if (kind.feedType === 'webhook_trade_fills') return { strategyName: dataName };
        else config = { strategyName: dataName, kairosSymbol: symbol.trim().toUpperCase() };
        return withStrategyInstrument(config, symbol, kindId);
      };
      const body = buildSubmissionPayload({
        entitySlug,
        displayName: name,
        slug,
        strategyName: dataName,
        strategyKind: kindId,
        visibility: 'public', // members see it straight away; one switch hides it
        description: '',
        submitterNotes: '',
        kairosSymbol: symbol,
        webhookStrategyId: slug,
        nt8StrategyName: name,
        csvText: hasCsv ? csv.text : '',
        csvFileName: csv.name,
        equityColumn: '',
        buildDataSourceConfig,
      });
      const res = await fetch(API_ENDPOINTS.adminStrategySubmissions(), { method: 'POST', headers: authJson(), body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t create the strategy');
      const newSlug = data.submission?.slug || data.submission?.proposedSlug || slug;

      // Results/Simulator visibility too (one switch), and live data on a CSV futures strategy.
      await fetch(API_ENDPOINTS.adminBrandStrategyVisibility(newSlug), { method: 'POST', headers: authJson(), body: JSON.stringify({ visible: true }) }).catch(() => {});
      // CSV daily-balance strategies get a URL automatically; only show it if asked for.
      let url = wantLive ? (data.webhookUrl || null) : null;
      if (wantLive && !url && hasCsv && liveAllowedWithCsv) {
        const live = await fetch(API_ENDPOINTS.adminBrandStrategyConnectLive(newSlug), { method: 'POST', headers: authJson() });
        const liveData = await live.json().catch(() => ({}));
        if (live.ok && liveData.success) url = liveData.url;
      }
      setCreated({ slug: newSlug, url });
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <Modal title={`${name} is ready`} onClose={onClose} wide>
        <p className="bs-note is-ok">Created and visible to your members.{hasCsv ? ` ${csv.rows} rows of results imported.` : ''}</p>
        {created.url ? (
          <LiveSetup url={created.url} kind={kindId === 'stocks' ? 'stock' : kindId === 'stock_trades' ? 'trades' : kindId === 'equity_curve' ? 'equity' : 'futures'} name={name} symbol={symbol} slug={created.slug} onTested={onCreated} />
        ) : null}
        <div className="bs-actions"><button type="button" className="bs-btn bs-btn-primary" onClick={onClose}>Done</button></div>
      </Modal>
    );
  }

  return (
    <Modal title="Add strategy" onClose={onClose} wide>
      <ol className="bs-steps" aria-label="Steps">
        {['What is it?', 'Past results', 'Live data'].map((label, i) => (
          <li key={label} className={i === step ? 'is-current' : i < step ? 'is-done' : ''}>{label}</li>
        ))}
      </ol>

      {step === 0 ? (
        <div>
          <div className="bs-kinds">
            {KINDS.map((k) => (
              <button type="button" key={k.id} className={`bs-kind${kindId === k.id ? ' is-on' : ''}`} onClick={() => setKindId(k.id)}>
                <strong>{k.title}</strong>
                <span className="bs-kind-sub">{k.subtitle}</span>
                <span className="bs-muted bs-small">{k.blurb}</span>
              </button>
            ))}
          </div>
          <div className="bs-grid2">
            <label className="bs-field">
              <span>Strategy name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Morning Breakout" autoFocus />
            </label>
            <label className="bs-field">
              <span>{kind.symbolLabel}</span>
              <input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder={kind.symbolPlaceholder} />
            </label>
          </div>
        </div>
      ) : null}

      {step === 1 && kind.liveOnly ? (
        <div>
          <p className="bs-muted">
            History for this type comes from the trades you send: every buy and sell is stored, paired into closed trades
            and charted. Older trades can be sent the same way (with their original "time").
          </p>
        </div>
      ) : null}

      {step === 1 && !kind.liveOnly ? (
        <div>
          <p className="bs-muted">Optional. Upload the strategy’s history so members see its results from day one. {kind.csvHelp}</p>
          <label className="bs-drop">
            <input type="file" accept=".csv,text/csv" onChange={pickFile} />
            {hasCsv ? <span><strong>{csv.name}</strong> · {csv.rows} rows</span> : <span>Choose a CSV file</span>}
          </label>
          {hasCsv ? <button type="button" className="bs-link" onClick={() => setCsv({ text: '', name: '', rows: 0 })}>Remove file</button> : null}
          <CsvPreview preview={preview} />
        </div>
      ) : null}

      {step === 2 ? (
        <div>
          {hasCsv && !liveAllowedWithCsv ? (
            <p className="bs-muted">
              Stock strategies made from a CSV update from the file. To use live TradingView alerts instead, go back and
              create it without a CSV.
            </p>
          ) : (
            <label className="bs-check">
              <input type="checkbox" checked={wantLive} onChange={(e) => setWantLive(e.target.checked)} />
              <span>
                <strong>Connect live data</strong>
                <span className="bs-muted bs-small">
                  {' '}You’ll get this strategy’s own webhook URL and the exact message to send, with a button to test it.
                </span>
              </span>
            </label>
          )}
          <p className="bs-muted bs-small">The strategy will be visible to your members. You can hide it any time.</p>
          {hasCsv ? <CsvPreview preview={preview} /> : null}
        </div>
      ) : null}

      {error ? <p className="bs-note is-bad" role="alert">{error}</p> : null}
      <div className="bs-actions">
        {step > 0 ? <button type="button" className="bs-btn" onClick={() => { setError(''); setStep((s) => s - 1); }} disabled={busy}>Back</button> : null}
        {step < 2
          ? <button type="button" className="bs-btn bs-btn-primary" onClick={next}>Next</button>
          : <button type="button" className="bs-btn bs-btn-primary" onClick={create} disabled={busy}>{busy ? 'Creating…' : 'Create strategy'}</button>}
      </div>
    </Modal>
  );
}

export default function BrandStrategies({ onNavigate }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  // An old "Create strategy" / "New live strategy" link opens Add strategy. The
  // flag is cleared when the dialog closes: the page can mount twice on load.
  const [adding, setAddingState] = useState(() => {
    try { return Boolean(window.sessionStorage.getItem(OPEN_ADD_STRATEGY_KEY)); } catch (_e) { return false; }
  });
  const setAdding = (open) => {
    setAddingState(open);
    if (!open) { try { window.sessionStorage.removeItem(OPEN_ADD_STRATEGY_KEY); } catch (_e) { /* nothing to clear */ } }
  };
  const [liveFor, setLiveFor] = useState(null);
  const [splitFor, setSplitFor] = useState(null); // row whose backtested / live split is being turned on
  const [busySlug, setBusySlug] = useState('');
  // Search / filter / sort (remembered in this browser).
  const [query, setQuery] = useState('');
  const [pane, setPane] = useState(() => {
    try { return window.localStorage.getItem(PANE_KEY) === 'webhooks' ? 'webhooks' : 'strategies'; } catch (_e) { return 'strategies'; }
  });
  const [feedFocus, setFeedFocus] = useState(null);
  const showPane = (next, focus = null) => {
    setPane(next);
    setFeedFocus(focus);
    try { window.localStorage.setItem(PANE_KEY, next); } catch (_e) { /* private mode */ }
  };
  const [view, setView] = useState(() => {
    try { return { kind: 'all', visibility: 'all', data: 'all', sort: 'name', ...JSON.parse(window.localStorage.getItem('risklo.brandStrategies.view') || '{}') }; } catch (_e) { return { kind: 'all', visibility: 'all', data: 'all', sort: 'name' }; }
  });
  const setViewKey = (k, v) => setView((cur) => {
    const next = { ...cur, [k]: v };
    try { window.localStorage.setItem('risklo.brandStrategies.view', JSON.stringify(next)); } catch (_e) { /* private mode */ }
    return next;
  });

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.adminBrandStrategies, { headers: getBearerAuthHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t load strategies');
      setRows(data.strategies || []);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // live status keeps itself current
    return () => clearInterval(t);
  }, [load]);

  const setVisible = async (row, visible) => {
    setBusySlug(row.slug);
    try {
      // Strategies with data but no record: their switch is the Results
      // visibility (the same one Arrange tabs and Sheet Manager set).
      const res = row.source === 'data'
        ? await fetch(API_ENDPOINTS.adminSheetsVisibility, { method: 'POST', headers: authJson(), body: JSON.stringify({ name: row.dataName, visibility: visible ? 'public' : 'hidden' }) })
        : await fetch(API_ENDPOINTS.adminBrandStrategyVisibility(row.slug), { method: 'POST', headers: authJson(), body: JSON.stringify({ visible }) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t change visibility');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusySlug('');
    }
  };

  const setLiveSplitOff = async (row) => {
    setBusySlug(row.slug);
    try {
      const res = await fetch(API_ENDPOINTS.adminBrandStrategyLiveSince(row.slug), {
        method: 'PUT', headers: authJson(), body: JSON.stringify({ liveSince: null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t turn off the split');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusySlug('');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.name}? Members stop seeing it and its live URL stops working.`)) return;
    setBusySlug(row.slug);
    try {
      const res = await fetch(`${API_ENDPOINTS.adminScenarios}/${encodeURIComponent(row.slug)}`, { method: 'DELETE', headers: getBearerAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t delete');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusySlug('');
    }
  };

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (rows || []).filter((r) => {
      if (q && !`${r.name} ${r.symbol || ''} ${KIND_LABEL[r.kind] || ''}`.toLowerCase().includes(q)) return false;
      if (view.kind !== 'all' && r.kind !== view.kind) return false;
      if (view.visibility === 'visible' && !r.visible) return false;
      if (view.visibility === 'hidden' && r.visible) return false;
      const data = r.liveStatus === 'live' ? 'live' : r.liveStatus === 'waiting' ? 'waiting' : 'uploaded';
      if (view.data !== 'all' && data !== view.data) return false;
      return true;
    });
    const byName = (a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base', numeric: true });
    const sorters = {
      name: byName,
      'name-desc': (a, b) => byName(b, a),
      recent: (a, b) => (Date.parse(b.lastDataAt || 0) || 0) - (Date.parse(a.lastDataAt || 0) || 0) || byName(a, b),
      visible: (a, b) => Number(b.visible) - Number(a.visible) || byName(a, b),
      kind: (a, b) => String(KIND_LABEL[a.kind] || '').localeCompare(String(KIND_LABEL[b.kind] || '')) || byName(a, b),
    };
    return [...list].sort(sorters[view.sort] || byName);
  }, [rows, query, view]);
  const filtering = Boolean(query.trim()) || view.kind !== 'all' || view.visibility !== 'all' || view.data !== 'all';
  const counts = useMemo(() => {
    const c = { visible: 0, live: 0 };
    (rows || []).forEach((r) => { if (r.visible) c.visible += 1; if (r.liveStatus === 'live') c.live += 1; });
    return c;
  }, [rows]);

  // Opens Strategy Data on this strategy (it reads ?scenario=<slug>).
  const editData = (row) => {
    if (!onNavigate) return;
    onNavigate(row.source === 'data'
      ? `admin-scenario-data?strategy=${encodeURIComponent(row.dataName)}`
      : `admin-scenario-data?scenario=${encodeURIComponent(row.slug)}`);
  };

  return (
    <div className={`bs-page${pane === 'webhooks' ? ' is-wide' : ''}`}>
      <div className="bs-head">
        <div>
          <h1>Strategies</h1>
          <p className="bs-muted">
            {pane === 'webhooks'
              ? 'Every webhook coming in, which strategy it feeds, and the trades and alerts received. Use it to check where data comes from.'
              : 'Add your strategies, connect their live data, and choose what your members see.'}
          </p>
        </div>
        <div className="bs-head-actions">
          {onNavigate ? (
            <button
              type="button"
              className="bs-btn bs-btn-lg"
              onClick={() => onNavigate('admin-scenario-data')}
              title="Edit any strategy's trades and data, the Master Strat composition and the Equity Portfolio"
            >
              Strategy Data
            </button>
          ) : null}
          {pane === 'strategies' ? (
            <button type="button" className="bs-btn bs-btn-primary bs-btn-lg" onClick={() => setAdding(true)}>+ Add strategy</button>
          ) : null}
        </div>
      </div>

      <div className="bs-panes" role="tablist" aria-label="Strategies sections">
        <button type="button" role="tab" aria-selected={pane === 'strategies'} className={`bs-pane-tab${pane === 'strategies' ? ' is-on' : ''}`} onClick={() => showPane('strategies')}>Strategies</button>
        <button type="button" role="tab" aria-selected={pane === 'webhooks'} className={`bs-pane-tab${pane === 'webhooks' ? ' is-on' : ''}`} onClick={() => showPane('webhooks')}>Webhook activity</button>
      </div>

      {pane === 'webhooks' ? (
        <Suspense fallback={<p className="bs-muted">Loading…</p>}>
          <WebhookActivity
            key={feedFocus?.slug || 'all'}
            focus={feedFocus}
            onClearFocus={() => setFeedFocus(null)}
          />
        </Suspense>
      ) : (
      <>

      {error ? <p className="bs-note is-bad" role="alert">{error}</p> : null}

      {rows && rows.length === 0 ? (
        <div className="bs-empty">
          <h3>No strategies yet</h3>
          <p className="bs-muted">Add your first strategy: upload its past results, connect live data, or both.</p>
          <button type="button" className="bs-btn bs-btn-primary" onClick={() => setAdding(true)}>+ Add strategy</button>
        </div>
      ) : null}

      {rows && rows.length ? (
        <div className="bs-toolbar">
          <div className="bs-search">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search strategies by name or symbol"
              aria-label="Search strategies"
            />
          </div>
          <div className="bs-filters">
            <select value={view.kind} onChange={(e) => setViewKey('kind', e.target.value)} aria-label="Type">
              <option value="all">All types</option>
              <option value="futures">Futures</option>
              <option value="stock">Stock / ETF</option>
              <option value="trades">Stock trades</option>
              <option value="equity">Daily balance</option>
            </select>
            <select value={view.visibility} onChange={(e) => setViewKey('visibility', e.target.value)} aria-label="Visibility">
              <option value="all">Visible & hidden</option>
              <option value="visible">Visible to members</option>
              <option value="hidden">Hidden</option>
            </select>
            <select value={view.data} onChange={(e) => setViewKey('data', e.target.value)} aria-label="Data">
              <option value="all">Any data</option>
              <option value="live">Live</option>
              <option value="waiting">Waiting for first data</option>
              <option value="uploaded">Uploaded results only</option>
            </select>
            <select value={view.sort} onChange={(e) => setViewKey('sort', e.target.value)} aria-label="Sort">
              <option value="name">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
              <option value="recent">Latest live data</option>
              <option value="visible">Visible first</option>
              <option value="kind">Type</option>
            </select>
          </div>
          <p className="bs-muted bs-small bs-count">
            {filtering ? `Showing ${shown.length} of ${rows.length}` : `${rows.length} strategies`} · {counts.visible} visible · {counts.live} live
            {filtering ? (
              <button type="button" className="bs-linkbtn" onClick={() => { setQuery(''); setViewKey('kind', 'all'); setViewKey('visibility', 'all'); setViewKey('data', 'all'); }}>Clear filters</button>
            ) : null}
          </p>
        </div>
      ) : null}

      {rows && rows.length && !shown.length ? <p className="bs-muted">No strategies match. Try a different search or clear the filters.</p> : null}

      {rows && shown.length ? (
        <div className="bs-list">
          {shown.map((row) => (
            <div className="bs-row" key={row.slug}>
              <div className="bs-row-main">
                <div className="bs-row-title">
                  {row.source === 'data' ? <strong>{row.name}</strong> : (
                    <StrategyNameEditor slug={row.slug} name={row.name} onRenamed={load} />
                  )}
                  <span className="bs-chip">{KIND_LABEL[row.kind] || 'Strategy'}</span>
                  {row.symbol ? <span className="bs-chip is-muted">{row.symbol}</span> : null}
                  {row.visibility === 'internal' ? <span className="bs-chip is-muted" title="Admins only">Internal</span> : null}
                </div>
                <LiveStatus row={row} />
              </div>
              <div className="bs-row-switches">
                <label
                  className={`bs-switch${row.visible ? ' is-on' : ''}`}
                  title={row.source === 'data'
                    ? 'Show this strategy to your members on Results, the summary and the Simulator'
                    : 'Show this strategy to your members on Overview, Results and the Simulator'}
                >
                  <input type="checkbox" checked={row.visible} disabled={busySlug === row.slug} onChange={(e) => setVisible(row, e.target.checked)} />
                  <span className="bs-switch-track" aria-hidden="true"><span className="bs-switch-knob" /></span>
                  {row.visible ? 'Visible to members' : 'Hidden'}
                </label>
                {row.source === 'data' ? null : (
                  <div className="bs-split">
                    <label
                      className={`bs-switch${row.liveSince ? ' is-on' : ''}`}
                      title="Show this strategy’s backtested results separately from its live results"
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(row.liveSince)}
                        disabled={busySlug === row.slug}
                        onChange={(e) => (e.target.checked ? setSplitFor(row) : setLiveSplitOff(row))}
                      />
                      <span className="bs-switch-track" aria-hidden="true"><span className="bs-switch-knob" /></span>
                      Backtested / live split
                    </label>
                    {row.liveSince ? (
                      <span className="bs-muted bs-small bs-split-note">
                        Live from {row.liveSince}{row.liveSinceFromData ? ' (from the data)' : ''}
                        {' · '}<button type="button" className="bs-linkbtn" onClick={() => setSplitFor(row)}>Change</button>
                      </span>
                    ) : row.detectedLiveSince ? (
                      <span className="bs-muted bs-small bs-split-note">Data marks live from {row.detectedLiveSince}</span>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="bs-row-actions">
                {row.source === 'data' ? null : (
                  <button type="button" className="bs-btn" onClick={() => setLiveFor(row)}>{row.hasWebhook || mappedSource(row) ? 'Live data' : 'Connect live data'}</button>
                )}
                <button type="button" className="bs-btn" onClick={() => editData(row)}>Edit data</button>
                {row.source === 'data' ? null : (
                  <button type="button" className="bs-btn bs-btn-danger" onClick={() => remove(row)} disabled={busySlug === row.slug}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {!rows && !error ? <p className="bs-muted">Loading…</p> : null}
      </>
      )}

      {adding ? <AddStrategy onClose={() => setAdding(false)} onCreated={load} /> : null}
      {splitFor ? <LiveSplitDialog row={splitFor} onClose={() => setSplitFor(null)} onSaved={load} /> : null}
      {liveFor ? (
        <ConnectLive
          row={liveFor}
          onClose={() => setLiveFor(null)}
          onChanged={load}
          onViewFeed={(focus) => { setLiveFor(null); showPane('webhooks', focus); }}
        />
      ) : null}
    </div>
  );
}
